import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ObjectTypes, ApiErrorCodes, ApiActions, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import queryString from 'query-string';
import {useGenericResultsDataApi} from '../../../api/hooks';
import Api from '../../../api/SMPFetchHelper';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import {GatewayGetListSchema, GatewayConnectedStatuses, GatewayFilterSchema, GatewayHAStatuses} from '../../../api/DBSchema/Gateway';
import {Link} from 'react-router-dom';
import store from '../../../store';
import connectedLogo from '../../../assets/images/gateway/connected.png';
import disconnectedLogo from '../../../assets/images/gateway/disconnected.png';
import disabledLogo from '../../../assets/images/gateway/disabled.png';
import haOkLogo from '../../../assets/images/ha_ok.png';
import haWarningLogo from '../../../assets/images/ha_warning.png';
import haErrorLogo from '../../../assets/images/ha_error.png';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {useSnackbar} from 'notistack';
import ToExcelSnackbarContent from '../../PopupPages/ToExcelSnackbarContent';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCLink from '../../../components/Simple/SMCLink';
import {dateToString, doSeamlessLogin} from '../../../utils/utils';
import {buildExistsSort, buildNumberSort, buildStringSortCaseInsensitive, buildWeighedSort} from '../../../utils/sortUtils';
import SMCHighlighter from '../../../components/Simple/SMCHighlighter';
import EditObjectCell from '../../../components/core/SMCTable/CommonCells/EditObjectCell';


const gatewayStatusCell = React.memo(({value, object}) => {

    const internetStatus = object[GatewayGetListSchema.internetStatus];

    const InternetStatusResult = () => {
        let internetStatusResult;
        switch (internetStatus) {
            case "HIGH QUALITY":
                internetStatusResult = <img src={haOkLogo} alt="ok" title="Internet Status: Ok" style={{marginLeft: '7px'}}/>;
                break;
            case "MEDIUM QUALITY":
                internetStatusResult = <img src={haWarningLogo} alt="warning" title="Internet Status: Problem Detected" style={{marginLeft: '5px'}}/>;
                break;
            case "LOW QUALITY":
                internetStatusResult = <img src={haErrorLogo} alt="error" title="Internet Status: Problem Detected" style={{marginLeft: '5px'}} />;
                break;
            default:
                internetStatusResult = null;
                break;
        }
        return (internetStatusResult);
    }
    
    switch (value) {
        case GatewayConnectedStatuses.connected:
            return (
                <div align='left'>                
                    <img src={connectedLogo} alt="Connected" title="Connected"/>
                    <InternetStatusResult/>
                </div>
                );
        case GatewayConnectedStatuses.disconnected:
            return (
                <div align='left'>         
                    <img src={disconnectedLogo} alt="disconnected" title="Disconnected"/>
                </div>
                );       
        case GatewayConnectedStatuses.disabled:
            return (
                <div align='left'>         
                    <img src={disabledLogo} alt="disabled" title="Disabled"/>
                </div>
                );
        default:
            return null;
    }
});

const gatewayStatusWeights = {
    [GatewayConnectedStatuses.connected]: 2,
    [GatewayConnectedStatuses.disconnected]: 1,
    [GatewayConnectedStatuses.disabled]: 0
}

const gatewayHAStatusCell = React.memo(({value}) => {
    switch (value) {
        case GatewayHAStatuses.ok:
            return <img src={haOkLogo} alt="ok" title="Ok" />;
            case GatewayHAStatuses.warning:
                return <img src={haWarningLogo} alt="warning" title="Warning" />;
            case GatewayHAStatuses.error:
                return <img src={haErrorLogo} alt="error" title="Error" />;
        default:
            return null;
    }
});


const accessGatewayCell = React.memo(({object, textToHighlight}) => {
    const ipAddress = object[GatewayGetListSchema.gatewayIp];
    const accessGatewayEnabled = object[GatewayGetListSchema.accessGatewayEnabled];
    const name = object[GatewayGetListSchema.name];
    
    if (!ipAddress && !accessGatewayEnabled) {
        return null;
    }
    return (
        <RowLayout wrap="nowrap">
            { ipAddress && <SMCLink onClick={() => doSeamlessLogin(name, 'ip')}><SMCHighlighter highlight={textToHighlight} text={ipAddress} /></SMCLink> }
            { ipAddress && accessGatewayEnabled && <span style={{ paddingLeft: '4px', paddingRight: '4px' }}>|</span> }
            { accessGatewayEnabled && <SMCLink onClick={() => doSeamlessLogin(name, 'reachMyDevice')}>Reach My Device</SMCLink> }
        </RowLayout>
    )
});

const nearestExperationDateCell = React.memo(({value}) => {
    return (
        value ? <span>{dateToString(value)}</span> : null
    )
});

const userCell = React.memo(({value, textToHighlight}) => {
    return (
        value ? <Link to={`${SMCPaths.users}?id=${value}`}><SMCHighlighter highlight={textToHighlight} text={value} /></Link> : null
    )
});

const planCell = React.memo(({value, textToHighlight}) => {
    return (
        value ? <Link to={`${SMCPaths.plans}?id=${value}`}><SMCHighlighter highlight={textToHighlight} text={value} /></Link> : null
    )
});

export function useGatewayResults(urlParams) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueSnackbar } = useSnackbar();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('name', SortDirections.ASCENDING);
    const [customFieldsNames,setCustomFieldsNames] = useState([])

    const columns = useMemo(() => {
      const basicColumns = [
          { id: 'status',                accessor: GatewayGetListSchema.status,                           header: t('SMC_GATEWAY_STATUS'),           name: t('SMC_GATEWAY_STATUS'), width: 75,  align: 'left', defaultShow: true,  sort: buildWeighedSort(GatewayGetListSchema.status, gatewayStatusWeights),      Cell: gatewayStatusCell         },
          { id: 'haStatus',              accessor: GatewayGetListSchema.haStatus,                         header: 'HA',                                                               width: 45,  align: 'left', defaultShow: false, sort: buildExistsSort(GatewayGetListSchema.haStatus),                           Cell: gatewayHAStatusCell       },
          { id: 'name',                  accessor: GatewayGetListSchema.name,                             header: t('SMP.NAME'),                                                      width: 150,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.name),                Cell: EditObjectCell      },
          { id: 'description',           accessor: GatewayGetListSchema.description,                      header: t('SMP.DESCRIPTION'),                                               width: 140,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.description)                                          },
          { id: 'humanReadableAddress',  accessor: GatewayGetListSchema.humanReadableAddress,             header: t('SMC_GATEWAYS_HUMAN_READABLE_ADDRESS'),                           width: 192,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.humanReadableAddress)                                 },
          { id: 'accessGateway',         accessor: GatewayGetListSchema.accessGateway,                    header: t('SMP.ACCESS_GATEWAY'),                                            width: 230,                  defaultShow: true,                                                                                  Cell: accessGatewayCell         },
          { id: 'macAddress',            accessor: GatewayGetListSchema.macAddress,                       header: t('SMP.MAC_ADDRESS'),                                               width: 130,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.macAddress)                                           },
          { id: 'licenseGeneralStatus',  accessor: GatewayGetListSchema.licenseGeneralStatus,             header: 'License Status',                                                   width: 130,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.licenseGeneralStatus)                                 },
          { id: 'nearestExpirationDate', accessor: GatewayGetListSchema.nearestExpirationDate,            header: 'Earliest Expiration',                                              width: 145,                  defaultShow: true,  sort: buildNumberSort(GatewayGetListSchema.nearestExpirationDate),              Cell: nearestExperationDateCell },
          { id: 'owner',                 accessor: GatewayGetListSchema.ownerId,                          header: t('SMC_GATEWAY_OWNER'),                                             width: 115,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.ownerId),             Cell: userCell                  },
          { id: 'gatewayType',           accessor: GatewayGetListSchema.gatewayType,                      header: t('SMC_GATEWAYS_TYPE'),                                             width: 160,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.gatewayType)                                          },
          { id: 'reportedFirmware',      accessor: GatewayGetListSchema.reportedFirmware,                 header: t('SMC_GATEWAYS_REPORTED_FIRMWARE'),                                width: 172,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.reportedFirmware)                                     },
          { id: 'plan',                  accessor: GatewayGetListSchema.planId,                           header: t('SMC_GATEWAYS_PLAN'),                                             width: 110,                  defaultShow: true,  sort: buildStringSortCaseInsensitive(GatewayGetListSchema.planId),              Cell: planCell                  },
      ]

        /*
        add custom fields columns if exists
         */
        if (customFieldsNames[0]){
            let custom1 = { id: 'custom1',               accessor: GatewayGetListSchema.custom1,                          header: customFieldsNames[0],                                                    width: 110,                  defaultShow: false, sort: buildStringSortCaseInsensitive(GatewayGetListSchema.custom1)                                              }
            basicColumns.push(custom1)
        }

        if (customFieldsNames[1]){
            let custom2 = { id: 'custom2',               accessor: GatewayGetListSchema.custom2,                          header:customFieldsNames[1],                                                     width: 110,                  defaultShow: false, sort: buildStringSortCaseInsensitive(GatewayGetListSchema.custom2)                                              }
            basicColumns.push(custom2)
        }

        return basicColumns;
    }, [t,customFieldsNames]);

    const rowIdSelector = obj => obj[GatewayGetListSchema.name];

    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.GATEWAY, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.GATEWAY, null);
    
    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const queryParams = queryString.parse(urlParams);

    const defaultFilter = useMemo(() => {
        const filterObject = {};
        if (queryParams) {
            if (queryParams.status === 'connected') {
                filterObject[GatewayFilterSchema.connected]    = {value: true };
                filterObject[GatewayFilterSchema.disconnected] = {value: false};
                filterObject[GatewayFilterSchema.disabled]     = {value: false};
            }
            if (queryParams.status === 'disconnected') {
                filterObject[GatewayFilterSchema.connected]    = {value: false};
                filterObject[GatewayFilterSchema.disconnected] = {value: true };
                filterObject[GatewayFilterSchema.disabled]     = {value: false};
            }
            if (queryParams.status === 'disabled') {
                filterObject[GatewayFilterSchema.connected]    = {value: false};
                filterObject[GatewayFilterSchema.disconnected] = {value: false};
                filterObject[GatewayFilterSchema.disabled]     = {value: true };
            }
        }
        return filterObject;
    }, [queryParams]);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects,
        filterObject,
        setFilterObject,
        freeTextSearch
    } = useGenericResultsDataApi(ObjectTypes.GATEWAY, undefined, undefined, defaultFilter);

    const newObjectOnSuccess = useCallback((objectId) => {
        if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
        }
    }, []);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
            setIsPageLoaded(false);
            deleteSelectedObjects()
                    .then(({successMessage, errorMessage}) => {
                        successMessage && enqueueSuccessSnackbar(successMessage);
                        errorMessage && enqueueErrorSnackbar(errorMessage);
                        reloadTable();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoaded(true);
                    });
        }
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    const doAction = useCallback(async (actionType, actionCode) => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        try {
            await Api.doAction(ObjectTypes.GATEWAY, actionType, selectedObjects);
            enqueueSuccessSnackbar(t('SMC_GATEWAY_CMD_SENDING', [t(actionCode)]));
            // const resp = await Api.doAction(ObjectTypes.GATEWAY, actionType, selectedObjects);
            // const respData = resp.data;
            // const respDataResults = respData.results;
            // const successCnt = respDataResults.filter(res => res.success).length;
            // let errorCnt = respDataResults.length - successCnt;
            // if (successCnt > 0) {
            //     enqueueSuccessSnackbar('Success: ' + successCnt);
            // }
            // if (errorCnt > 0) {
            //     enqueueErrorSnackbar('Error: ' + errorCnt);
            // }
        } catch (e) {
            const errorMessage = getErrorMessage(e);
            alert(errorMessage);
        }
    }, [selectedObjects, getErrorMessage, enqueueSuccessSnackbar, t]);

    const resetToPlanAction = useCallback(() => {
        doAction(ApiActions.gateway.resetToPlan, 'ACTION_RESET_TOPLAN');
    }, [doAction]);

    const toExcelAction = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        enqueueSnackbar(null, {
            persist: true,
            content: (key) => (
                <ToExcelSnackbarContent
                    list={selectedObjects}
                    objectType="GATEWAY"
                    id={key}
                />
            )
        })
    }, [selectedObjects, enqueueSnackbar, t]);

    const rebootAction = useCallback(() => {

        if (window.confirm(t('RESULTS_OBJECTS_REBOOT_QUES'))) {
            doAction(ApiActions.gateway.reboot, 'com.sofaware.cmd.CMD_GATEWAYREBOOT');
        }
        
    }, [doAction, t]);

    const fetchAction = useCallback(() => {
        doAction(ApiActions.gateway.fetch, 'com.sofaware.cmd.CMD_GATEWAY_FETCH');
    }, [doAction]);

    const backupAction = useCallback(() => {
        doAction(ApiActions.gateway.backup, 'com.sofaware.cmd.CMD_GATEWAY_BACKUP');
    }, [doAction]);

    const sendDailyReportAction = useCallback(() => {
        doAction(ApiActions.gateway.sendDailyReport, 'com.sofaware.cmd.CMD_GATEWAY_SEND_DAILY_REPORT');
    }, [doAction]);

    const sendWeeklyReportAction = useCallback(() => {
        doAction(ApiActions.gateway.sendWeeklyReport, 'com.sofaware.cmd.CMD_GATEWAY_SEND_WEEKLY_REPORT');
    }, [doAction]);

    const sendMonthlyReportAction = useCallback(() => {
        doAction(ApiActions.gateway.sendMonthlyReport, 'com.sofaware.cmd.CMD_GATEWAY_SEND_MONTHLY_REPORT');
    }, [doAction]);

    // Reload table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
        Api.fetchServiceDomainGatewayCustomFieldsNames().then(res=>{
            if (res.data.data){
                setCustomFieldsNames(res.data.data)
            }
        }).catch(e=>{
            console.log(e.message)
        })
    }, [reloadTable, enqueueErrorSnackbar, t]);
    
    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);

    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        newObjectOnSuccess,
        reloadButtonOnClick,
        deleteButtonOnClick,
        resetToPlanAction,
        toExcelAction,
        rebootAction,
        fetchAction,
        backupAction,
        sendDailyReportAction,
        sendWeeklyReportAction,
        sendMonthlyReportAction
    });
}